import Swal from 'sweetalert2'
import { mapMutations, mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import Stepper from '@/components/Stepper.vue'
import { deepDelete } from '@/helpers/deepDelete'
import LoadingDialog from '@/components/LoadingDialog.vue'
import BasePageLayout from '@/components/BasePageLayout.vue'
import StatusIcon from '@/modules/maria/campanha-da-maria/views/components/StatusIcon.vue'
import { QUERY_UPSERT_DRAFT_OMNI_CAMPAIGN, QUERY_UPSERT_OMNI_CAMPAIGN } from '@/modules/maria/campanha-da-maria/graphql'

export const mariaCampaignActions = {
  components: {
    Stepper,
    BasePageLayout,
    LoadingDialog,
    StatusIcon,
  },

  data: () => ({
    campaign: {
      version: 2,
      title: '',
      description: '',
      products: [],
      segments: [],
      senders: [],
      status: 'DRAFT',
      offer_sales: [],
      offerSelected: null,
      initial_date: null,
      final_date: null,
      shotConfiguration: {
        whatsapp: {
          name: 'Whatsapp',
          icon: 'whatsapp',
          credits: '',
          appChannelOptions: {
            toggleAppActivated: false,
            toggleAppExpanded: false,
          },
          targeting_mode: '',
          message: {
            text: '',
            send_date: '',
            send_time: '',
            attachment: {
              data_url: '',
              thumbnail_url: '',
              mimetype: '',
              filename: '',
            },
          },
        },
        sms: {
          name: 'SMS',
          icon: 'sms',
          appChannelOptions: {
            toggleAppActivated: false,
            toggleAppExpanded: false,
          },
          text: '',
          credits: '',
          error: false,
        },
        push: {
          name: 'Push',
          icon: 'smartphone',
          appChannelOptions: {
            toggleAppActivated: false,
            toggleAppExpanded: false,
          },
          title: '',
          text: '',
          inner_message_title: '',
          inner_message_text: '',
          innerMessage: false,
          image_url: '',
          action: {
            type: '',
            link: '',
            reference_id: null,
          },
        },
        email: {
          name: 'E-mail',
          icon: 'email',
          appChannelOptions: {
            toggleAppActivated: false,
            toggleAppExpanded: false,
          },
          credits: '',
          message: {
            send_date: '',
            send_time: '',
            email_configuration_type: '',
            processed_html: '',
            subject: '',
            simple_email_configuration: {
              subject: '',
              header_image: null,
              text: '',
              image: null,
              click_to_call_button_text: '',
              click_to_call_button_link: '',
              footer: '',
            },
            html_email_configuration: {
              url: '',
              filename: '',
              has_uploaded_file: '',
              uploaded_at: '',
            },
            builder_email_configuration: {},
          },
        },
      },
      personalized: false,
      budget: {
        clients: {
          client_active_count: 70,
          client_inactive_count: 15,
          client_lost_count: 10,
          client_never_bought_count: 5,
        },
        clientsDistribution: {
          actives: 60,
          at_risk: 10,
          losses: 10,
          never_bought: 5,
          recent_inactives: 15,
        },
        products: {
          rebuy: 100,
          upsell: 0,
        },
        advancedConfigs: {
          trigger: true,
          retrigger: true,
        },
      },
      message: {
        text: '',
        send_date: '',
        send_time: '',
        attachment: {
          data_url: '',
          thumbnail_url: '',
          mimetype: '',
          filename: '',
        },
      },
    },
    forcePush: false,
    loadingPage: false,
    loadingSaveDraft: false,
    loadingSaveActive: false,
  }),

  computed: {
    ...mapGetters(['mariaCampaign', 'units']),

    isMariaCampaignActivated() {
      return this.campaign.status === 'ACTIVE'
    },

    isMariaCampaignFinished() {
      return this.campaign.status && this.campaign.status === 'FINISHED'
    },

    getWhatsappType() {
      if (!this.campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppActivated) return ''
      const { mimetype } = this.campaign.shotConfiguration.whatsapp.message.attachment
      if (!mimetype) return 'TEXT'
      else if (mimetype === 'application/pdf') return 'DOCUMENT'
      else return 'IMAGE'
    },

    isCampaignActivated() {
      return ['ACTIVE', 'SCHEDULED'].includes(this.campaign.status)
    },
  },

  methods: {
    ...mapMutations(['SET_MARIA_CAMPAIGN', 'RESET_MARIA_CAMPAIGN_MODULE']),

    ...mapActions(['fetchUnits']),

    goTomariaCampaignIndexTable() {
      this.forcePush = true
      this.$router.push({ name: 'Mar.ia Campaign' })
    },

    formatOfferSales(data) {
      let offerSales = []
      data.forEach((offer) => {
        if (['CampaignOfferSalesGroup', 'OfferSalesGroup'].includes(offer.__typename) || offer.isGroup) {
          if (offer.offer_sales) {
            offerSales.push(
              ...offer.offer_sales.map((offerGroup) => {
                return {
                  ...offerGroup,
                  price_1: offer.price_1,
                  price_2: offer.price_2,
                  isGroup: true,
                  nameGroup: offer.name,
                  groupInfo: {
                    _id: offer._id,
                    __typename: 'ProductsGroup',
                  },
                }
              })
            )
          } else if (offer.products) {
            offerSales.push(
              ...offer.products.map((offerGroup) => {
                return {
                  ...offerGroup,
                  price_1: offer.price_1,
                  price_2: offer.price_2,
                  isGroup: true,
                  nameGroup: offer.name,
                  groupInfo: {
                    _id: offer._id,
                    __typename: 'ProductsGroup',
                  },
                }
              })
            )
          } else {
            offerSales.push(offer)
          }
        } else offerSales.push(offer)
      })
      return offerSales
    },

    formatOfferSalesPercentage(data) {
      let offerSalesPercentage = []
      if (!data) return offerSalesPercentage
      data.forEach((offer) => {
        if (offer.isGroup || ['CampaignOfferSalesPercentageGroup', 'OfferSalesPercentageGroup'].includes(offer.__typename)) {
          if (offer.percentage_offer_sales) {
            offerSalesPercentage.push(
              ...offer.percentage_offer_sales.map((offerGroup) => {
                return {
                  ...offerGroup,
                  isGroup: true,
                  nameGroup: offer.name,
                  percentage_discount: offer.percentage_discount,
                  groupInfo: {
                    _id: offer._id,
                    __typename: 'CampaignOfferSalesPercentageGroup',
                  },
                }
              })
            )
          } else if (offer.products) {
            offerSalesPercentage.push(
              ...offer.products.map((offerGroup) => {
                return {
                  ...offerGroup,
                  isGroup: true,
                  nameGroup: offer.name,
                  percentage_discount: offer.percentage_discount,
                  groupInfo: {
                    _id: offer._id,
                    __typename: 'CampaignOfferSalesPercentageGroup',
                  },
                }
              })
            )
          } else {
            offerSalesPercentage.push(offer)
          }
        } else offerSalesPercentage.push(offer)
      })
      return offerSalesPercentage
    },

    formatOfferSalesTakeAndPay(data) {
      let offerSalesTakeAndPay = []
      if (!data) return offerSalesTakeAndPay
      data.forEach((offer) => {
        if (offer.isGroup || ['CampaignOfferSalesTakeAndPayGroup', 'OfferSalesTakeAndPayGroup'].includes(offer.__typename)) {
          if (offer.takepay_offer_sales) {
            offerSalesTakeAndPay.push(
              ...offer.takepay_offer_sales.map((offerGroup) => {
                return {
                  ...offerGroup,
                  isGroup: true,
                  nameGroup: offer.name,
                  take: offer.take,
                  pay: offer.pay,
                  maximum_per_client: offer.maximum_per_client,
                  groupInfo: {
                    _id: offer._id,
                    __typename: 'CampaignOfferSalesTakeAndPayGroup',
                  },
                }
              })
            )
          } else if (offer.products) {
            offerSalesTakeAndPay.push(
              ...offer.products.map((offerGroup) => {
                return {
                  ...offerGroup,
                  isGroup: true,
                  nameGroup: offer.name,
                  take: offer.take,
                  pay: offer.pay,
                  maximum_per_client: offer.maximum_per_client,
                  groupInfo: {
                    _id: offer._id,
                    __typename: 'CampaignOfferSalesTakeAndPayGroup',
                  },
                }
              })
            )
          } else {
            offerSalesTakeAndPay.push(offer)
          }
        } else offerSalesTakeAndPay.push(offer)
      })
      return offerSalesTakeAndPay
    },

    formatOfferSalesCombo(data) {
      let offerSalesCombo = []
      if (!data) return offerSalesCombo
      data.forEach((combo) => {
        offerSalesCombo.push({
          ...combo,
          trigger_products: combo.trigger_products.map((triggerProducts) => {
            if (['CampaignTriggerProduct', 'TriggerProduct'].includes(triggerProducts.__typename))
              return { ...triggerProducts, __typename: 'CampaignTriggerProduct' }
            if (['CampaignTriggerProductGroup', 'TriggerProductGroup'].includes(triggerProducts.__typename))
              return {
                name: triggerProducts.name,
                quantity: triggerProducts.quantity,
                products: triggerProducts.trigger_products ? triggerProducts.trigger_products : triggerProducts.products,
                imported: false,
                isGroup: true,
                origin: 'search',
                original_eans: [],
                _id: triggerProducts._id,
                __typename: 'CampaignTriggerProductGroup',
              }
          }),
        })
      })
      return offerSalesCombo
    },

    offerComboArray(combos) {
      return combos.map((combo) => {
        combo.trigger_products = combo.trigger_products.reduce((accumulator, product) => {
          if (product.isGroup) {
            const formattedProducts = product.products.map((item) => {
              const { isGroup, product: productDetail, ...formattedProduct } = item
              const { _id } = productDetail

              return Object.assign(formattedProduct, {
                quantity: product.quantity,
                _id,
                product: productDetail,
                isGroup: true,
                nameGroup: product.name,
                groupInfo: { _id: product._id },
              })
            })
            accumulator.push(...formattedProducts)
          } else {
            accumulator.push(product)
          }

          return accumulator
        }, [])
        return combo
      })
    },

    async upsertDraftMariaCampaign(data, activateCampaign = false) {
      this.tryingToSaveAsDraft = activateCampaign
      if (this.validateInfos) {
        const isValid = await this.validateInfos()
        if (!isValid) return
      }

      await this.$apollo
        .mutate({
          mutation: activateCampaign ? QUERY_UPSERT_OMNI_CAMPAIGN : QUERY_UPSERT_DRAFT_OMNI_CAMPAIGN,
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
          variables: {
            data: this.preparePayload(data),
          },
        })
        .then(() => {
          Swal.fire({
            text: 'Campanha salva',
            icon: 'success',
            showConfirmButton: false,
            timer: 1000,
            customClass: {
              content: 'content-class',
            },
          })
          this.forcePush = true
          this.$router.push({ name: 'Mar.ia Campaign' })
        })
        .catch((error) => {
          let text = 'Erro ao salvar campanha'
          if (error.message === 'GraphQL error: Initial date and final date must be after the current date')
            text = 'Data inicial e data final da campanha deve ser igual ou maior à data atual.'

          Swal.fire({
            text,
            icon: 'error',
            showConfirmButton: false,
            timer: 1500,
            customClass: {
              content: 'content-class',
            },
          })
        })
    },

    isUndefined(value) {
      return typeof value === 'undefined'
    },
    validateInitialDate(data) {
      try {
        if (data?.initial_date) {
          const currentDate = DateTime.now().toFormat('yyyy-MM-dd')
          const currentTime = DateTime.now().toFormat('HH:mm:ss')

          if (currentDate < data.initial_date && !data?.start_time) {
            data.start_time = '00:00:00'
          }

          if (currentDate === data.initial_date && (data.start_time < currentTime || !data?.start_time)) {
            return `${data.initial_date} ${currentTime}`
          } else {
            return `${data.initial_date} ${data.start_time}`
          }
        } else return null
      } catch (error) {
        console.error('Unable to validate initial date: ', error)
      }
    },
    validateFinalDate(data) {
      try {
        return data?.final_date ? `${data.final_date} ${data?.end_time || '23:59:59'}` : null
      } catch (error) {
        console.error('Unable to validate final date: ', error)
      }
    },
    preparePayload(data) {
      // const initialDate = this.validateInitialDate(data)
      const finalDate = this.validateFinalDate(data)

      const { offerType } = data
      if (offerType === 'general') {
        data.offer_sales = data.offer_sales.map((offer) => {
          return {
            active: this.isUndefined(offer.active) ? true : offer.active,
            maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
            minimum_per_client: this.isUndefined(offer.minimum_per_client) ? 0 : offer.minimum_per_client,
            price_1: this.isUndefined(offer.price_1) ? 0 : offer.price_1,
            price_2: this.isUndefined(offer.price_2) ? 0 : offer.price_2,
            sku: offer.sku,
            eans: offer.eans,
            name: offer.name,
            ...(offer.isGroup
              ? {
                  group_name: offer.nameGroup,
                  group_id: offer.groupInfo._id,
                }
              : {}),
          }
        })
        data.percentage_offer_sales = []
        data.takepay_offer_sales = []
        data.combo_offer_sales = []
      }

      if (offerType === 'percentage') {
        data.percentage_offer_sales = data.percentage_offer_sales.map((offer) => {
          return {
            active: this.isUndefined(offer.active) ? true : offer.active,
            maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
            minimum_per_client: this.isUndefined(offer.minimum_per_client) ? 0 : offer.minimum_per_client,
            percentage_discount: this.isUndefined(offer.percentage_discount) ? 0 : offer.percentage_discount,
            sku: offer.sku,
            eans: offer.eans,
            name: offer.name,
            ...(offer.isGroup
              ? {
                  group_name: offer.nameGroup,
                  group_id: offer.groupInfo._id,
                }
              : {}),
          }
        })
        data.offer_sales = []
        data.takepay_offer_sales = []
        data.combo_offer_sales = []
      }

      if (offerType === 'takepay') {
        data.takepay_offer_sales = data.takepay_offer_sales.map((offer) => {
          return {
            active: this.isUndefined(offer.active) ? true : offer.active,
            maximum_per_client: this.isUndefined(offer.maximum_per_client) ? 0 : offer.maximum_per_client,
            take: this.isUndefined(offer.take) ? 0 : offer.take,
            pay: this.isUndefined(offer.pay) ? 0 : offer.pay,
            sku: offer.sku,
            eans: offer.eans,
            name: offer.name,
            ...(offer.isGroup
              ? {
                  group_name: offer.nameGroup,
                  group_id: offer.groupInfo._id,
                }
              : {}),
          }
        })
        data.offer_sales = []
        data.percentage_offer_sales = []
        data.combo_offer_sales = []
      }

      if (offerType === 'combo') {
        const combo = this.offerComboArray(data.combo_offer_sales).map((combo) => {
          let target_product = combo.target_products[0]
          if (!target_product?.sku && !target_product?.eans) {
            target_product = {
              ...target_product,
              sku: target_product?.target_products[0]?.sku,
              eans: target_product?.target_products[0]?.eans,
            }
          }

          return {
            name: combo.name,
            maximum_per_client: combo.maximum_per_client,
            image: combo.image,
            active: combo.active,
            combo_id: combo.combo_id,
            target_products: {
              name: target_product.name,
              sku: target_product.sku,
              eans: target_product.eans,
              percentage_discount: this.isUndefined(target_product.percentage_discount) ? 0 : target_product.percentage_discount,
              ...(target_product.isGroup
                ? {
                    group_name: target_product.nameGroup,
                    group_id: target_product.groupInfo._id,
                  }
                : {}),
            },
            trigger_products: combo.trigger_products.map((product) => {
              return {
                name: product.name,
                sku: product.sku,
                eans: product.eans,
                quantity: product.quantity,
                ...(product.isGroup
                  ? {
                      group_name: product.nameGroup,
                      group_id: product.groupInfo._id,
                    }
                  : {}),
              }
            }),
          }
        })
        data.combo_offer_sales = combo
        data.offer_sales = []
        data.percentage_offer_sales = []
        data.takepay_offer_sales = []
      }

      deepDelete(data, '__typename')

      return {
        _id: data._id,
        title: data.title,
        description: data.description,
        offer_schedule: data.offerSelected,
        offer_sales: data.offer_sales,
        percentage_offer_sales: data.percentage_offer_sales,
        takepay_offer_sales: data.takepay_offer_sales,
        combo_offer_sales: data.combo_offer_sales,
        // temporarily fix
        ...(data.initial_date ? { initial_date: `${data.initial_date} 00:00:00` } : {}),
        final_date: finalDate,
        units: data.units,
        units_configuration: data.units_configuration,
        segments: data.segments.map((segment) => segment._id),
        client_targets: data.client_targets,
        client_targets_distribution: data.client_targets_distribution,
        product_targets: data.product_targets,
        advanced_configuration: data.advanced_configuration,
        channels: data.channels,
        selected_offer_type: [offerType],
      }
    },

    getPartialPayload() {
      let partialPayload = {}
      if (this.campaign.budget) {
        if (this.campaign.version === 2 || !this.campaign._id) {
          partialPayload['client_targets_distribution'] = {
            actives: this.campaign.budget.clientsDistribution.actives / 100,
            at_risk: this.campaign.budget.clientsDistribution.at_risk / 100,
            losses: this.campaign.budget.clientsDistribution.losses / 100,
            never_bought: this.campaign.budget.clientsDistribution.never_bought / 100,
            recent_inactives: this.campaign.budget.clientsDistribution.recent_inactives / 100,
          }
        } else {
          partialPayload['client_targets'] = {
            inactive: this.campaign.budget.clients.client_inactive_count / 100,
            active: this.campaign.budget.clients.client_active_count / 100,
            lost: this.campaign.budget.clients.client_lost_count / 100,
            never_bought: this.campaign.budget.clients.client_never_bought_count / 100,
          }
        }
        partialPayload['product_targets'] = {
          repurchase: this.campaign.budget.products.rebuy / 100,
          upsell: this.campaign.budget.products.upsell / 100,
        }
        partialPayload['advanced_configuration'] = {
          multiple_channels_per_consumer: this.campaign.budget.advancedConfigs.trigger,
          multiple_messages_per_consumer: this.campaign.budget.advancedConfigs.retrigger,
        }
      }
      if (this.campaign.shotConfiguration) {
        const attachment = {
          data_url: '',
          thumbnail_url: '',
          mimetype: '',
          filename: '',
        }

        partialPayload['channels'] = {
          ...(this.campaign.shotConfiguration.whatsapp.appChannelOptions.toggleAppActivated
            ? {
                whatsapp: {
                  active: true,
                  budget: parseInt(this.campaign.shotConfiguration.whatsapp.credits.replaceAll('.', '')),
                  senders: this.campaign.senders.map((sender) => (sender.number ? sender.number : sender)),
                  targeting_mode: this.campaign.shotConfiguration.whatsapp.targeting_mode,
                  message: {
                    send_time: '08:00:00',
                    send_date: this.campaign.shotConfiguration.whatsapp.message.send_date || this.campaign.initial_date,
                    text: this.campaign.shotConfiguration.whatsapp.message.text,
                    attachment: this.getWhatsappType === 'TEXT' ? attachment : this.campaign.shotConfiguration.whatsapp.message.attachment,
                    type: this.getWhatsappType,
                  },
                },
              }
            : {}),
          ...(this.campaign.shotConfiguration.sms.appChannelOptions.toggleAppActivated
            ? {
                sms: {
                  active: true,
                  budget: parseInt(this.campaign.shotConfiguration.sms.credits.replaceAll('.', '')),
                  message: {
                    text: this.campaign.shotConfiguration.sms.text,
                    send_time: '08:00:00',
                    send_date: this.campaign.shotConfiguration.sms.send_date || this.campaign.initial_date,
                  },
                },
              }
            : {}),
          ...(this.campaign.shotConfiguration.push.appChannelOptions.toggleAppActivated
            ? {
                push: {
                  active: true,
                  budget: parseInt(this.campaign.shotConfiguration.push.credits.replaceAll('.', '')),
                  message: {
                    text: this.campaign.shotConfiguration.push.text,
                    send_time: '08:00:00',
                    send_date: this.campaign.shotConfiguration.push.send_date || this.campaign.initial_date,
                    inner_message_text: this.campaign.shotConfiguration.push.innerMessage
                      ? this.campaign.shotConfiguration.push.inner_message_text
                      : this.campaign.shotConfiguration.push.text,
                    inner_message_title: this.campaign.shotConfiguration.push.innerMessage
                      ? this.campaign.shotConfiguration.push.inner_message_title
                      : this.campaign.shotConfiguration.push.title,
                    title: this.campaign.shotConfiguration.push.title,
                    image_url: this.campaign.shotConfiguration.push.image_url,
                    action: this.campaign.shotConfiguration.push.action,
                  },
                },
              }
            : {}),
          ...(this.campaign.shotConfiguration.email.appChannelOptions.toggleAppActivated
            ? {
                email: {
                  active: true,
                  budget: parseInt(this.campaign.shotConfiguration.email.credits.replaceAll('.', '')),
                  message: {
                    send_date: this.campaign.shotConfiguration.email.message.send_date || this.campaign.initial_date,
                    send_time: '08:00:00',
                    subject: this.campaign.shotConfiguration.email.message.subject,
                    email_configuration_type: this.campaign.shotConfiguration.email.message.email_configuration_type,
                    processed_html: this.campaign.shotConfiguration.email.message.processed_html,
                    simple_email_configuration: {
                      subject: this.campaign.shotConfiguration.email.message.simple_email_configuration.subject,
                      header_image: this.campaign.shotConfiguration.email.message.simple_email_configuration.header_image,
                      text: this.campaign.shotConfiguration.email.message.simple_email_configuration.text,
                      image: this.campaign.shotConfiguration.email.message.simple_email_configuration.image,
                      click_to_call_button_link: this.campaign.shotConfiguration.email.message.simple_email_configuration.click_to_call_button_link,
                      click_to_call_button_text: this.campaign.shotConfiguration.email.message.simple_email_configuration.click_to_call_button_text,
                      footer: this.campaign.shotConfiguration.email.message.simple_email_configuration.footer,
                    },
                    html_email_configuration: {
                      url: this.campaign.shotConfiguration.email.message.html_email_configuration.url,
                      filename: this.campaign.shotConfiguration.email.message.html_email_configuration.filename,
                      has_uploaded_file: this.campaign.shotConfiguration.email.message.html_email_configuration.has_uploaded_file,
                      uploaded_at: this.campaign.shotConfiguration.email.message.html_email_configuration.uploaded_at,
                    },
                    builder_email_configuration: this.campaign.shotConfiguration.email.message.builder_email_configuration,
                  },
                },
              }
            : {}),
        }
      }
      if (partialPayload?.channels?.email?.message?.email_configuration_type === 'SIMPLE') {
        delete partialPayload?.channels?.email?.message?.html_email_configuration
        delete partialPayload?.channels?.email?.message?.builder_email_configuration
      } else if (partialPayload?.channels?.email?.message?.email_configuration_type === 'BUILDER') {
        delete partialPayload?.channels?.email?.message?.simple_email_configuration
        delete partialPayload?.channels?.email?.message?.html_email_configuration
      } else {
        delete partialPayload?.channels?.email?.message?.simple_email_configuration
        delete partialPayload?.channels?.email?.message?.builder_email_configuration
      }
      return partialPayload
    },

    redirectCampaigns() {
      this.RESET_MARIA_CAMPAIGN_MODULE()
      this.$router.push({ name: 'Mar.ia Campaign' })
    },

    async tryingToSaveCampaign() {
      this.loadingSaveActive = true
      try {
        await this.saveCampaign(true)
      } catch (e) {
        this.showError()
      } finally {
        this.loadingSaveActive = false
      }
    },

    async tryingToSaveDraft() {
      this.loadingSaveDraft = true
      try {
        await this.saveCampaign(this.isCampaignActivated)
      } catch (e) {
        this.showError()
      } finally {
        this.loadingSaveDraft = false
      }
    },

    showError() {
      this.$snackbar({ message: 'Ocorreu um erro ao salvar campanha.', snackbarColor: 'error' })
      this.$nextTick(() => {
        this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
      })
    },
  },

  beforeRouteLeave(to, from, next) {
    if (this.offerScheduleFinalizedId && this.offerScheduleFinalizedId === this.campaign.offerSelected) {
      this.$confirm({
        title: 'Gestão de oferta desvinculada ',
        message:
          'A gestão de ofertas vinculada à essa campanha foi concluída e por isso não pode mais ser utilizada. Selecione outra oferta ou produtos para continuar sua campanha.',
        confirmText: 'Entendi',
        hideCancel: true,
      }).then(() => {
        this.offerScheduleFinalizedId = ''
        this.campaign.offerSelected = null
      })
    } else if (!this.forcePush && !this.isMariaCampaignFinished) {
      this.$confirm({
        title: 'Tem certeza?',
        message:
          'Sua campanha ainda não foi salva e todas as suas alterações recentes serão perdidas ao sair dessa página. Para continuar editando clique em <strong>continuar editando</strong>, para sair sem salvar clique em <strong>descartar alterações</strong>.',
        confirmText: 'Continuar editando',
        cancelText: 'Descartar alterações',
      })
        .then()
        .catch(() => {
          next()
        })
    } else {
      next()
    }
  },
}
